import type { Card, ccv3 } from '@proj-airi/ccc'

import { nanoid } from 'nanoid'
import { defineStore, storeToRefs } from 'pinia'
import { computed, watch } from 'vue'

import { getTranslator } from '../../utils/i18n-bridge'
import { useIndexedDBStorage, waitForStorage } from '../../utils/indexed-db-storage'
import { useConsciousnessStore } from './consciousness'
import { useSpeechStore } from './speech'

export interface AiriExtension {
  modules: {
    consciousness: {
      model: string // Example: "gpt-4o"
    }

    speech: {
      model: string // Example: "eleven_multilingual_v2"
      voice_id: string // Example: "alloy"

      pitch?: number
      rate?: number
      ssml?: boolean
      language?: string
    }

    vrm?: {
      source?: 'file' | 'url'
      file?: string // Example: "vrm/model.vrm"
      url?: string // Example: "https://example.com/vrm/model.vrm"
    }

    live2d?: {
      source?: 'file' | 'url'
      file?: string // Example: "live2d/model.json"
      url?: string // Example: "https://example.com/live2d/model.json"
    }
  }

  agents: {
    [key: string]: { // example: minecraft
      prompt: string
    }
  }
}

export interface AiriCard extends Card {
  extensions: {
    airi: AiriExtension
  } & Card['extensions']
}

export const useAiriCardStore = defineStore('meowchat-card', () => {
  // 使用 IndexedDB 存储代替 localStorage，支持更大的存储容量
  const cardsStorage = useIndexedDBStorage<Record<string, AiriCard>>('meowchat-cards', {}, {
    serializer: {
      read: (v: string) => {
        try {
          const data = JSON.parse(v)
          // 验证数据格式，过滤掉无效数据
          const validCards: Record<string, AiriCard> = {}
          for (const [id, card] of Object.entries(data)) {
            if (card && typeof card === 'object' && (card as any).name) {
              validCards[id] = card as AiriCard
            }
          }
          return validCards
        }
        catch {
          return {}
        }
      },
      write: (v: Record<string, AiriCard>) => JSON.stringify(v),
    },
    onError: (error) => {
      console.error('❌ 角色卡存储错误:', error)
    },
  })

  // 使用 IndexedDB 存储活动卡片ID
  const activeCardIdStorage = useIndexedDBStorage<string>('meowchat-card-active-id', 'default')

  // 创建响应式引用
  const cards = cardsStorage.data
  const activeCardId = activeCardIdStorage.data

  // 存储加载状态
  const isLoaded = computed(() => cardsStorage.isLoaded.value && activeCardIdStorage.isLoaded.value)
  const isLoading = computed(() => cardsStorage.isLoading.value || activeCardIdStorage.isLoading.value)

  const activeCard = computed(() => cards.value[activeCardId.value])

  const consciousnessStore = useConsciousnessStore()
  const speechStore = useSpeechStore()

  const {
    activeModel: activeConsciousnessModel,
  } = storeToRefs(consciousnessStore)

  const {
    activeSpeechVoiceId,
    activeSpeechModel,
  } = storeToRefs(speechStore)

  const addCard = (card: AiriCard | Card | ccv3.CharacterCardV3) => {
    const newCardId = nanoid()
    // 创建新对象以触发响应式更新
    cards.value = {
      ...cards.value,
      [newCardId]: newAiriCard(card),
    }
    return newCardId
  }

  const removeCard = (id: string) => {
    // ⚠️ 级联删除：删角色卡前，先删掉所有引用这张卡的联系人及其全部数据
    // （聊天/记忆/表情/背景/关系/朋友圈/通话），否则这些联系人会变成「无卡孤儿」，
    // 其残留记忆会经跨人格共享/迁移泄漏进其他角色（如「袁基说出斯卡蒂台词」）。
    // 用动态 import 避免循环依赖（与 contacts.deleteContact 的跨 store 清理同款模式）。
    import('../contacts').then(({ useContactsStore }) => {
      const contactsStore = useContactsStore()
      const affected = contactsStore.getContactsByCardId(id)
      let successCount = 0
      for (const contact of affected) {
        try {
          contactsStore.deleteContact(contact.id)
          successCount++
        }
        catch (e) {
          console.warn(`⚠️ 删除角色卡 ${id}：级联删除联系人 ${contact.id} 失败:`, e)
        }
      }
      if (affected.length > 0)
        console.info(`✅ 删除角色卡 ${id}：已级联删除 ${successCount}/${affected.length} 个引用它的联系人`)
    }).catch((err) => {
      console.warn(`⚠️ 删除角色卡 ${id} 的级联联系人清理失败:`, err)
    })

    // 从 cards map 删掉卡片对象本身
    const { [id]: _removed, ...rest } = cards.value
    cards.value = rest

    // 若删除的正是当前激活卡，重置到默认卡，避免活跃状态指向已删除的卡
    if (activeCardId.value === id)
      activeCardId.value = 'default'
  }

  const updateCard = (id: string, updates: Partial<AiriCard>) => {
    const existingCard = cards.value[id]
    if (!existingCard)
      return false

    // 深度合并，确保嵌套对象也被正确处理
    const updatedCard: AiriCard = {
      ...existingCard,
      ...updates,
      // 特殊处理 metadata，确保不覆盖未更新的字段
      metadata: updates.metadata
        ? { ...existingCard.metadata, ...updates.metadata }
        : existingCard.metadata,
      // 特殊处理 extensions，确保不覆盖未更新的字段
      extensions: updates.extensions
        ? {
            airi: {
              ...existingCard.extensions?.airi,
              ...updates.extensions?.airi,
              modules: {
                ...existingCard.extensions?.airi?.modules,
                ...updates.extensions?.airi?.modules,
              },
              agents: {
                ...existingCard.extensions?.airi?.agents,
                ...updates.extensions?.airi?.agents,
              },
            },
          }
        : existingCard.extensions,
    }

    // 创建新对象以触发响应式更新
    cards.value = {
      ...cards.value,
      [id]: updatedCard,
    }
    return true
  }

  const getCard = (id: string) => {
    return cards.value[id]
  }

  /**
   * 设置角色的分组
   */
  function setCardGroup(cardId: string, groupId: string | null): boolean {
    const card = cards.value[cardId]
    if (!card)
      return false

    // 创建新的 metadata 对象
    const newMetadata = { ...card.metadata }
    if (groupId === null) {
      // 如果 groupId 为 null，则删除该字段
      delete newMetadata.groupId
    }
    else {
      newMetadata.groupId = groupId
    }

    return updateCard(cardId, {
      metadata: newMetadata,
    })
  }

  /**
   * 批量导出角色卡
   */
  function exportCards(cardIds?: string[]): string {
    const cardsToExport = cardIds
      ? cardIds.map(id => cards.value[id]).filter(Boolean)
      : Object.values(cards.value)

    return JSON.stringify({
      version: '1.0',
      exportTime: Date.now(),
      cards: cardsToExport,
    }, null, 2)
  }

  /**
   * 批量导入角色卡
   */
  function importCards(jsonData: string): { success: number, failed: number, errors: string[] } {
    const errors: string[] = []
    let success = 0
    let failed = 0

    try {
      const data = JSON.parse(jsonData)

      if (!data.cards || !Array.isArray(data.cards)) {
        throw new Error('无效的导入数据格式')
      }

      for (const cardData of data.cards) {
        try {
          addCard(cardData as AiriCard)
          success++
        }
        catch (error) {
          failed++
          errors.push(`导入角色失败: ${error}`)
        }
      }
    }
    catch (error) {
      errors.push(`解析数据失败: ${error}`)
      return { success: 0, failed: 0, errors }
    }

    return { success, failed, errors }
  }

  function resolveAiriExtension(card: Card | ccv3.CharacterCardV3): AiriExtension {
    // Get existing extension if available
    const existingExtension = ('data' in card
      ? card.data?.extensions?.airi
      : card.extensions?.airi) as AiriExtension

    // Create default modules config
    const defaultModules = {
      consciousness: {
        model: activeConsciousnessModel.value,
      },
      speech: {
        model: activeSpeechModel.value,
        voice_id: activeSpeechVoiceId.value,
      },
    }

    // Return default if no extension exists
    if (!existingExtension) {
      return {
        modules: defaultModules,
        agents: {},
      }
    }

    // Merge existing extension with defaults
    return {
      modules: {
        consciousness: {
          model: existingExtension.modules?.consciousness?.model ?? defaultModules.consciousness.model,
        },
        speech: {
          model: existingExtension.modules?.speech?.model ?? defaultModules.speech.model,
          voice_id: existingExtension.modules?.speech?.voice_id ?? defaultModules.speech.voice_id,
          pitch: existingExtension.modules?.speech?.pitch,
          rate: existingExtension.modules?.speech?.rate,
          ssml: existingExtension.modules?.speech?.ssml,
          language: existingExtension.modules?.speech?.language,
        },
        vrm: existingExtension.modules?.vrm,
        live2d: existingExtension.modules?.live2d,
      },
      agents: existingExtension.agents ?? {},
    }
  }

  function newAiriCard(card: Card | ccv3.CharacterCardV3): AiriCard {
    // Handle ccv3 format if needed
    if ('data' in card) {
      const ccv3Card = card as ccv3.CharacterCardV3
      return {
        name: ccv3Card.data.name,
        version: ccv3Card.data.character_version ?? '1.0.0',
        description: ccv3Card.data.description ?? '',
        creator: ccv3Card.data.creator ?? '',
        notes: ccv3Card.data.creator_notes ?? '',
        notesMultilingual: ccv3Card.data.creator_notes_multilingual,
        personality: ccv3Card.data.personality ?? '',
        scenario: ccv3Card.data.scenario ?? '',
        greetings: [
          ccv3Card.data.first_mes,
          ...(ccv3Card.data.alternate_greetings ?? []),
        ],
        greetingsGroupOnly: ccv3Card.data.group_only_greetings ?? [],
        systemPrompt: ccv3Card.data.system_prompt ?? '',
        postHistoryInstructions: ccv3Card.data.post_history_instructions ?? '',
        messageExample: ccv3Card.data.mes_example
          ? ccv3Card.data.mes_example
              .split('<START>\n')
              .filter(Boolean)
              .map(example => example.split('\n')
                .map((line) => {
                  if (line.startsWith('{{char}}:') || line.startsWith('{{user}}:'))
                    return line as `{{char}}: ${string}` | `{{user}}: ${string}`
                  throw new Error(`Invalid message example format: ${line}`)
                }))
          : [],
        tags: ccv3Card.data.tags ?? [],
        extensions: {
          airi: resolveAiriExtension(ccv3Card),
          ...ccv3Card.data.extensions,
        },
      }
    }

    return {
      ...card,
      extensions: {
        airi: resolveAiriExtension(card),
        ...card.extensions,
      },
    }
  }

  /**
   * 等待存储加载完成
   */
  async function waitForLoad(): Promise<void> {
    await Promise.all([
      waitForStorage(cardsStorage),
      waitForStorage(activeCardIdStorage),
    ])
  }

  // 初始化：存储加载完成后，确保默认卡片存在
  // 使用 watch 代替 onMounted，因为 store 可能在组件上下文外创建
  watch(isLoaded, (loaded) => {
    if (!loaded)
      return
    if (cards.value.default)
      return

    // 使用 i18n 桥接获取翻译函数（支持非组件上下文）
    const t = getTranslator()

    cards.value = {
      ...cards.value,
      default: newAiriCard({
        name: 'Pluto',
        version: '1.0.0',
        description: t('base.prompt.prefix'),
      }),
    }
  }, { immediate: true })

  watch(activeCard, (newCard: AiriCard | undefined) => {
    if (!newCard)
      return

    // TODO: live2d, vrm
    // TODO: Minecraft Agent, etc
    const extension = resolveAiriExtension(newCard)
    if (!extension)
      return

    // 🔧 注意：LLM 模型（consciousness.model）是全局设置，不应该跟随角色卡变化
    // 角色卡只应该保存角色相关的设置（如 2D/3D 模型、TTS 语音等）
    // 因此这里不再同步 consciousness.model

    // TTS 模型和语音可以跟随角色卡变化（每个角色可以有不同的声音）
    if (extension?.modules?.speech?.model) {
      activeSpeechModel.value = extension.modules.speech.model
    }
    if (extension?.modules?.speech?.voice_id) {
      activeSpeechVoiceId.value = extension.modules.speech.voice_id
    }
  })

  return {
    cards,
    activeCard,
    activeCardId,
    addCard,
    removeCard,
    updateCard,
    getCard,
    setCardGroup,
    exportCards,
    importCards,
    waitForLoad,

    // 存储状态
    isLoaded,
    isLoading,

    currentModels: computed(() => {
      return {
        consciousness: {
          model: activeConsciousnessModel.value,
        },
        speech: {
          model: activeSpeechModel.value,
          voice_id: activeSpeechVoiceId.value,
        },
      } satisfies AiriExtension['modules']
    }),

    systemPrompt: computed(() => {
      const card = activeCard.value
      if (!card)
        return ''

      const components = [
        card.systemPrompt,
        card.description,
        card.personality,
      ].filter(Boolean)

      return components.join('\n')
    }),

    // AI 角色名称
    characterName: computed(() => {
      const card = activeCard.value
      return card?.name || 'ReLU'
    }),
  }
})
